import React, {useMemo} from "react";
import {Stack, Typography} from "@mui/material";
import {useAssignedDeskStore} from "../../../api/AssignedDeskStore";
import {useEmployeeVisitStore} from "../../../api/EmployeeVisitStore";
import {useEmployeeNeighbourhoodStore} from "../../../api/EmployeeNeighbourhoodStore";
import EmployeeAvatar from "../../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../../components/employee-link/EmployeeLink";
import NeighbourhoodColorIcon from "../../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {DateRangeOptions} from "../../../components/filter-fields/DateRangeFilter";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {filterDateRange} from "../../../util/FilterUtil";
import {RemoteValue} from "../../../util/RemoteValue";
import {filterOfficeVisitReport} from "../../../util/visit/VisitReportSheetUtil";
import {
    Desks_Read,
    Employees_Read,
    KeyData_Read,
    Neighbourhoods_Read,
    NewJoiners_Read,
    VisitReports_Read,
} from "../../../util/access/Permission";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {
    findRegularOfficeEmployeesWithoutAssignedDesks,
    RegularOfficeEmployeeWithoutAssignedDesk,
} from "./RegularOfficeEmployeesWithoutAssignedDesks";
import {WidgetEmployeeVisitFigure} from "../WidgetFigure";

const dateRange = DateRangeOptions.lastFourMonths;

const csvModel = RemoteValue.fromValue(new CsvModel<RegularOfficeEmployeeWithoutAssignedDesk>([
    {header: 'Person', value: ({employee}) => employee.fullName},
    {header: 'Neighbourhoods', value: ({neighbourhoods}) => neighbourhoods.map(({name}) => name).join(', ')},
    {header: 'Workday visits', value: ({visits}) => visits.workDayVisits.toString()},
    {header: 'Eligible workdays', value: ({visits}) => visits.tenureDayCount.toString()},
    {header: 'Visit rate', value: ({visits}) => `${visits.percent}%`},
]));

export function RegularOfficeEmployeesWithoutAssignedDesksWidget() {
    const assignedDesks = useAssignedDeskStore(state => state.remoteValue);
    const employeeVisits = useEmployeeVisitStore(state => state.remoteValue);
    const employeeNeighbourhoods = useEmployeeNeighbourhoodStore(state => state.remoteValue);

    const problems = useMemo(() => RemoteValue
            .combine({assignedDesks, employeeVisits, employeeNeighbourhoods})
            .map(({assignedDesks, employeeVisits, employeeNeighbourhoods}) => findRegularOfficeEmployeesWithoutAssignedDesks(
                filterOfficeVisitReport(employeeVisits, filterDateRange(dateRange)),
                assignedDesks,
                employeeNeighbourhoods,
            )),
        [assignedDesks, employeeVisits, employeeNeighbourhoods],
    );

    return (
        <DashboardWidget
            title="Regular office employees without assigned desks"
            description="Active employees who visited on at least 70% of eligible workdays in the last four months but do not have an assigned desk."
            problems={problems}
            requiredPermissions={[Desks_Read, Employees_Read, KeyData_Read, Neighbourhoods_Read, NewJoiners_Read, VisitReports_Read]}
            csvModel={csvModel}
            renderProblem={({employee, neighbourhoods, visits}, index) => (
                <DashboardWidgetItem
                    key={employee.id}
                    index={index}
                    left={(
                        <>
                            <EmployeeAvatar employee={employee} sx={{width: 24, height: 24}}/>
                            <EmployeeLink employee={employee}/>
                            {neighbourhoods.map(neighbourhood => (
                                <Stack key={neighbourhood.key} direction="row" alignItems="center" gap={0.5}>
                                    <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                                    <Typography variant="body2">{neighbourhood.name}</Typography>
                                </Stack>
                            ))}
                        </>
                    )}
                    right={(
                        <WidgetEmployeeVisitFigure employeeVisits={visits}/>
                    )}
                />
            )}
        />
    );
}
